import { useEffect, useState, useCallback } from "react";
import { AdminLayout } from "./AdminLayout";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Badge, qualClass } from "@/components/StatusBadge";
import { PROGRAMS, QUALIFICATION_OPTIONS, PROGRESS_OPTIONS, US_STATES } from "@/data/intakeConfig";
import { Search, Download, Filter } from "lucide-react";
import { toast } from "sonner";

const PROGRAM_SHORT = {
  "Class A CDL through 160 Driving Academy": "CDL",
  "AAWTA Certified Dispatcher Program": "Dispatcher",
  "WGU Behind-the-Desk Certificate": "WGU",
  "Mentorship and Career Pathway Support": "Mentorship",
};

export default function Applicants() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ by: "created_at", dir: "desc" });
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ search: "", program: "", qualification: "", progress_status: "", state: "", advisor: "" });
  const navigate = useNavigate();
  const pageSize = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), page, page_size: pageSize, sort_by: sort.by, sort_dir: sort.dir };
      const res = await api.get("/admin/applicants", { params });
      setRows(res.data.items);
      setTotal(res.data.total);
    } catch { toast.error("Failed to load applicants"); }
    finally { setLoading(false); }
  }, [f, page, sort]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [f, sort]);

  const toggleSort = (col) => setSort((s) => ({ by: col, dir: s.by === col && s.dir === "asc" ? "desc" : "asc" }));
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export/csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "aawta_applicants.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const Select = ({ name, placeholder, options }) => (
    <select value={f[name]} onChange={(e) => setF({ ...f, [name]: e.target.value })}
      data-testid={`filter-${name}`}
      className="border border-navy/15 rounded-md px-3 py-2 text-sm bg-white text-navy focus:ring-2 focus:ring-magenta outline-none">
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  return (
    <AdminLayout title="Applicants">
      <div className="bg-white rounded-lg border border-navy/10 shadow-sm p-4 mb-5">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 text-navy/40" size={18} />
            <input value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })}
              data-testid="applicant-search"
              placeholder="Search name, email, phone, reference…"
              className="w-full pl-10 pr-3 py-2.5 border border-navy/15 rounded-md text-sm focus:ring-2 focus:ring-magenta outline-none" />
          </div>
          <button onClick={exportCsv} data-testid="export-csv"
            className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white text-sm font-semibold py-2.5 px-4 rounded-md transition-colors">
            <Download size={16} /> Export CSV
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <span className="inline-flex items-center gap-1 text-xs text-navy/50 font-semibold"><Filter size={14} /> Filters:</span>
          <Select name="program" placeholder="Program" options={PROGRAMS.map((p) => p.id)} />
          <Select name="qualification" placeholder="Qualification" options={QUALIFICATION_OPTIONS} />
          <Select name="progress_status" placeholder="Progress" options={PROGRESS_OPTIONS} />
          <Select name="state" placeholder="State" options={US_STATES} />
          <input value={f.advisor} onChange={(e) => setF({ ...f, advisor: e.target.value })}
            placeholder="Advisor" className="border border-navy/15 rounded-md px-3 py-2 text-sm w-32 focus:ring-2 focus:ring-magenta outline-none" data-testid="filter-advisor" />
        </div>
      </div>

      <div className="bg-white rounded-lg border border-navy/10 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="applicants-table">
            <thead className="bg-navy-light text-navy">
              <tr>
                {[["reference_number", "Reference"], ["name", "Name"], [null, "Contact"], [null, "Programs"], ["cdl_score", "CDL Score"], ["qualification", "Qualification"], ["progress_status", "Progress"]].map(([col, h]) => (
                  <th key={h} onClick={col ? () => toggleSort(col) : undefined}
                    className={`text-left font-semibold px-4 py-3 whitespace-nowrap ${col ? "cursor-pointer select-none hover:text-magenta" : ""}`}
                    data-testid={col ? `sort-${col}` : undefined}>
                    {h}{col && sort.by === col ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-navy/5">
              {loading ? (
                <tr><td colSpan={7} className="p-8 text-center text-dark-text/50">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-dark-text/50">No applicants match your filters.</td></tr>
              ) : rows.map((a) => (
                <tr key={a.applicant_id} onClick={() => navigate(`/admin/applicants/${a.applicant_id}`)}
                  className="hover:bg-navy-light/40 cursor-pointer transition-colors" data-testid={`applicant-row-${a.reference_number}`}>
                  <td className="px-4 py-3 font-mono text-xs text-navy font-semibold whitespace-nowrap">{a.reference_number}</td>
                  <td className="px-4 py-3 font-semibold text-navy whitespace-nowrap">{a.name}</td>
                  <td className="px-4 py-3 text-dark-text/70"><div>{a.email}</div><div className="text-xs">{a.phone}</div></td>
                  <td className="px-4 py-3"><div className="flex flex-wrap gap-1 max-w-[240px]">{a.programs.map((p) => <span key={p} title={p} className="bg-navy-light text-navy text-[10px] px-2 py-0.5 rounded-full">{PROGRAM_SHORT[p] || p}</span>)}</div></td>
                  <td className="px-4 py-3 font-bold text-magenta">{a.cdl_score ?? "—"}</td>
                  <td className="px-4 py-3"><Badge className={qualClass(a.qualification)}>{a.qualification}</Badge></td>
                  <td className="px-4 py-3 text-dark-text/70 whitespace-nowrap">{a.progress_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-navy/10 text-sm" data-testid="pagination">
          <span className="text-dark-text/60">{total === 0 ? "0" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)}`} of {total}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="page-prev"
              className="px-3 py-1.5 rounded-md border border-navy/15 font-semibold text-navy disabled:opacity-40 hover:bg-navy-light transition-colors">Prev</button>
            <span className="text-navy font-semibold">Page {page} / {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="page-next"
              className="px-3 py-1.5 rounded-md border border-navy/15 font-semibold text-navy disabled:opacity-40 hover:bg-navy-light transition-colors">Next</button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
